import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';

import { FormControl, FormGroup } from '@angular/forms';
import { SharedLanguageService } from '@language';
import {
    ApiFragment,
    BaseContainer,
    DialogConfig,
    DialogRef,
    FormGroupStyling,
    FormSelectOption,
    NotificationType,
    Share,
    ShSharesDataService,
    ShToastFactory,
    toFormSelectOption,
    User,
    UserService
} from '@stylehaven/components';
import { ClipboardService } from 'ngx-clipboard';
import { EMPTY, Observable, ReplaySubject } from 'rxjs';
import { catchError, debounceTime, map, skip, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { StoreService } from '../../common/services/store/store.service';
import { MediahavenShareDialogConfig } from '../entities/share-dialog-config.entity';

@Component({
    standalone: false,
    selector: 'med-share-dialog',
    templateUrl: './mediahaven-share-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediahavenShareDialogComponent extends BaseContainer implements OnInit, OnDestroy {
    public readonly FormGroupStyling: typeof FormGroupStyling = FormGroupStyling;
    public readonly NotificationType: typeof NotificationType = NotificationType;
    public readonly data: MediahavenShareDialogConfig;

    // Labels
    public mainButtonKey: string = 'sharing.dialog.generating-link';

    // Conditionals
    public inProgress: boolean = false;
    public shouldFlush: boolean = false;

    // Data
    public records: readonly ApiFragment[];
    public share$: ReplaySubject<Share> = new ReplaySubject<Share>(1);

    // Template
    public rightsOptions$: Observable<FormSelectOption[]>;
    public minDate: string = new Date(Date.now() + 86400000).toISOString().split('T')[0];
    public errorKey: string | null = null;

    // Controls
    public readonly rightsControl: FormControl<'true' | 'false'> = new FormControl();
    public readonly loginRequiredControl: FormControl<boolean | null> = new FormControl();
    public readonly expirationControl: FormControl<Date | null> = new FormControl();
    public readonly form: FormGroup = new FormGroup({
        rights: this.rightsControl,
        loginRequired: this.loginRequiredControl,
        expiration: this.expirationControl
    });

    // Injectors
    private readonly toastFactory: ShToastFactory = inject(ShToastFactory);
    private readonly userService: UserService = inject(UserService);
    private readonly sharesService: ShSharesDataService = inject(ShSharesDataService);
    private readonly storeService: StoreService = inject(StoreService);
    private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly clipboard: ClipboardService = inject(ClipboardService);

    constructor(
        public config: DialogConfig<MediahavenShareDialogConfig>,
        public dialog: DialogRef<boolean>,
        public lang: SharedLanguageService
    ) {
        super();

        if (!config.data) {
            throw new Error('ShareDialog needs a configuration object');
        }
        this.data = config.data;
        this.records = this.data.records;
    }

    ngOnInit(): void {
        this.initLinkGeneration();
        this.initFormListener();
        this.initOptions();
    }

    private initLinkGeneration(): void {
        this.inProgress = true;

        this.sharesService
            .createShare(
                this.records.map(record => record.getFragmentId()),
                null,
                false,
                false
            )
            .pipe(
                catchError(() => {
                    this.errorKey = 'sharing.dialog.errors.creation-error';
                    this.changeDetector.markForCheck();
                    return EMPTY;
                }),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(share => {
                this.share$.next(share);
                this.mainButtonKey = 'sharing.dialog.copy-button';
                this.inProgress = false;
                this.changeDetector.markForCheck();
            });
    }

    private initFormListener(): void {
        this.form.valueChanges
            .pipe(
                skip(1),
                withLatestFrom(this.share$),
                tap(() => (this.shouldFlush = true)),
                debounceTime(500),
                switchMap(([formValue, share]) => {
                    return this.updateShare(share, formValue);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private initOptions(): void {
        this.rightsOptions$ = this.userService.getCurrentUser().pipe(map(user => this.initRightsOptions(user)));
    }

    private initRightsOptions(user: User): FormSelectOption[] {
        const hasExportRights: boolean =
            user.Rights.ExportRights &&
            this.records.every(record =>
                this.has1CommonElement(
                    record.getPermissions('Export'),
                    user.Groups.map(group => group.Id)
                )
            );

        return [
            toFormSelectOption('false', this.translateService.translate('sharing.dialog.options.rights.read')),
            toFormSelectOption(
                'true',
                this.translateService.translate('sharing.dialog.options.rights.read-download'),
                !hasExportRights,
                hasExportRights
                    ? ''
                    : this.translateService.translate('sharing.dialog.disabled-reason.no-export', {
                          amount: this.records.length
                      })
            )
        ];
    }

    private updateShare(
        share: Share,
        formValue: Partial<{ rights: 'true' | 'false'; loginRequired: boolean | null; expiration: string | null }>
    ): Observable<boolean> {
        return this.sharesService
            .updateShare(share.Id, {
                ...share,
                ExpiryDate: formValue.expiration ? formValue.expiration : null,
                RequiresLogin: !!formValue.loginRequired,
                Exportable: formValue.rights === 'true'
            })
            .pipe(
                tap(() => {
                    this.errorKey = null;
                    this.changeDetector.markForCheck();
                    this.shouldFlush = false;
                }),
                catchError(() => {
                    this.errorKey = 'sharing.dialog.errors.update-error';
                    this.inProgress = true;
                    this.changeDetector.markForCheck();
                    return EMPTY;
                }),
                take(1)
            );
    }

    copySuccess(): void {
        this.storeService.addToast(
            this.toastFactory.create({
                message: this.translateService.translate('action-toasts.copy-link.success'),
                type: NotificationType.Success
            })
        );
        this.dialog.close(true);
    }

    cancel(): void {
        this.dialog.close();
    }

    private has1CommonElement(list1: string[], list2: string[]): boolean {
        const set2: Set<string> = new Set(list2);
        return list1.some(element => set2.has(element));
    }

    ngOnDestroy(): void {
        // Make sure that the share is updated if the dialog is closed when there are still pending changes
        if (this.shouldFlush) {
            this.share$
                .pipe(
                    switchMap(share => this.updateShare(share, this.form.value)),
                    take(1)
                )
                .subscribe();
        }

        super.ngOnDestroy();
    }
}
